/**
 * DirectionPriorCard
 * Empirical over/under base rates (direction prior) for a prop in a league,
 * with a break-even reference. Calibration internals are intentionally not shown.
 */

import type { DirectionPriorCardData } from '@parlay-picker/shared/chatBettingCards';
import { getLeagueLabel, isLeagueKey } from '@parlay-picker/shared/league';
import { Scale } from 'lucide-react';
import type { ReactElement } from 'react';

const NO_DATA = '—';

export function DirectionPriorCard({ data }: { data: DirectionPriorCardData }): ReactElement {
  const { league, propType, breakeven, cells } = data;
  const breakevenPct = (breakeven * 100).toFixed(1);

  return (
    <div className="bg-theme-surface border border-theme-border rounded-xl p-3 space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Scale className="w-4 h-4 text-theme-primary" />
        <span className="text-sm font-semibold text-theme-text">{propType} base rates</span>
        <span className="text-[10px] text-theme-text-tertiary ml-auto">
          {isLeagueKey(league) ? getLeagueLabel(league) : league}
        </span>
      </div>

      {/* Column header */}
      <div className="grid grid-cols-3 gap-1 text-[9px] text-theme-text-tertiary">
        <span>Direction</span>
        <span className="text-center">Hit rate</span>
        <span className="text-right">Sample</span>
      </div>

      {/* One row per direction */}
      <div className="space-y-1">
        {cells.map((cell) => {
          const clears = cell.hitRate != null && cell.hitRate >= breakeven;
          const rateColor =
            cell.hitRate == null
              ? 'text-theme-text-tertiary'
              : clears
                ? 'text-theme-success'
                : 'text-theme-text';
          return (
            <div key={cell.direction} className="grid grid-cols-3 gap-1 items-center text-xs">
              <span className="text-theme-text capitalize">{cell.direction}</span>
              <span className={`text-center tabular-nums font-medium ${rateColor}`}>
                {cell.hitRate == null ? NO_DATA : `${(cell.hitRate * 100).toFixed(1)}%`}
              </span>
              <span className="text-right tabular-nums text-theme-text-tertiary text-[10px]">
                n={cell.sampleCount}
                {!cell.usable ? ' (low)' : ''}
              </span>
            </div>
          );
        })}
      </div>

      <div className="text-[10px] text-theme-text-tertiary">
        Break-even at standard juice is ~{breakevenPct}%. Rates above it are in green. "(low)" means
        the sample is too small to rely on yet.
      </div>
    </div>
  );
}
